// EntityChip — linked-entity pill (icon or avatar + label + optional meta),
// e.g. the client/contact chips in the lead sidebar. i18n-clean: `label`/
// `meta`/`title` arrive via props; imports only from `./` (ui), never a feature.
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { Avatar } from './Avatar.js';
import { Icon, type IconName } from './Icon.js';

// Discriminated union: exactly one of `icon`/`avatarName` is required —
// setting both, or neither, is a compile-time error.
export type EntityChipProps = {
  label: string;
  meta?: ReactNode;
  onClick?: () => void;
  title?: string;
} & ({ icon: IconName; avatarName?: undefined } | { icon?: undefined; avatarName: string });

const PILL_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  height: 30,
  padding: '0 10px',
  borderRadius: 'var(--r-full)',
  border: '1px solid var(--border-1)',
  fontSize: 13,
  color: 'var(--fg-1)',
  // Bound the inline-flex pill by its parent so the label can truncate;
  // without this the pill sizes to content and the ellipsis never engages.
  maxWidth: '100%',
};

export function EntityChip({ icon, avatarName, label, meta, onClick, title }: EntityChipProps) {
  const [hover, setHover] = useState(false);
  const clickable = onClick !== undefined;

  const pill: CSSProperties = {
    ...PILL_BASE,
    background: clickable && hover ? 'var(--bg-2)' : 'var(--bg-1)',
  };

  const content = (
    <>
      {icon ? (
        <Icon name={icon} size={14} style={{ color: 'var(--fg-3)' }} />
      ) : (
        <Avatar name={avatarName} size={18} />
      )}
      <span
        style={{
          // minWidth:0 lets this flex item shrink below its content width so
          // the ellipsis engages instead of the label expanding the pill.
          minWidth: 0,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      {meta !== undefined && <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{meta}</span>}
      {clickable && <Icon name="chevron-right" size={13} style={{ color: 'var(--fg-4)' }} />}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ ...pill, cursor: 'pointer', font: 'inherit', color: 'var(--fg-1)' }}
      >
        {content}
      </button>
    );
  }

  return (
    <span style={pill} title={title}>
      {content}
    </span>
  );
}
